/* 3DA quantity controls.
   Product pages: the +/- stepper sets the add-to-cart quantity (local only).
   Cart page: every change is saved to Shopify right away and the cart section is re-rendered
   from Shopify's own response, so line prices, Mix & Match discounts and the subtotal are always
   the real numbers. The first tap saves immediately; taps made while a save is running are batched
   into one follow-up request. Each row is one product (Shopify can split a product into several
   cart lines when a discount covers only part of the quantity); rows are tracked by a stable id
   and every change is spread across that row's current lines, because Shopify issues new line
   keys after each update. Without JavaScript the native cart form still works. */
(() => {
  'use strict';
  if (document.documentElement.dataset.quantityControlsReady) return;
  document.documentElement.dataset.quantityControlsReady = 'true';

  const root = document.querySelector('[data-auto-cart]');
  const sectionId = root ? root.dataset.sectionId : '';
  const cartUrl = (root && root.dataset.cartUrl) || '/cart';
  const updateUrl = (root && root.dataset.updateUrl) || '/cart/update.js';
  const pending = new Map(); // stable row id -> wanted total quantity for that row
  let inFlight = null;
  let lastError = null;
  let allowSubmit = false;
  let liveTimer = null;

  const lines = () => (root ? Array.from(root.querySelectorAll('[data-cart-line]')) : []);
  const lineById = (id) => lines().find((line) => line.dataset.lineId === id) || null;
  const lineTitle = (line) => {
    const link = line && line.querySelector('strong a');
    return link ? link.textContent.trim() : 'Item';
  };

  function announce(text) {
    const el = root && root.querySelector('[data-cart-live]');
    if (!el) return;
    el.textContent = text;
    clearTimeout(liveTimer);
    if (text) liveTimer = setTimeout(() => { el.textContent = ''; }, 4000);
  }

  function showError(text) {
    const el = root && root.querySelector('[data-cart-status]');
    if (el) el.textContent = text || '';
  }

  function setCount(count) {
    if (typeof count !== 'number' || Number.isNaN(count)) return;
    const link = document.querySelector('.cart-link');
    if (!link) return;
    let badge = link.querySelector('.cart-count');
    if (!count) {
      if (badge) badge.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'cart-count';
      link.appendChild(badge);
    }
    badge.textContent = String(count);
  }

  function sync(control) {
    const input = control.querySelector('input');
    const minus = control.querySelector('[data-quantity-step="-1"]');
    const plus = control.querySelector('[data-quantity-step="1"]');
    if (!input || !minus || !plus) return;
    const floor = control.closest('[data-cart-line]') ? 0 : Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    minus.disabled = Number(input.value) <= floor;
    plus.disabled = Number(input.value) >= max;
  }

  function enhance(scope) {
    scope.querySelectorAll('[data-tap-quantity]').forEach((control) => {
      const input = control.querySelector('input');
      if (!input) return;
      input.readOnly = true;
      control.querySelectorAll('button').forEach((button) => { button.hidden = false; });
      control.classList.add('is-ready');
      sync(control);
    });
    scope.querySelectorAll('[data-manual-update]').forEach((button) => { button.hidden = true; });
    // Changes are saved live, so the no-JavaScript quantity fields must not be resubmitted at checkout.
    scope.querySelectorAll('[data-cart-line] input[name^="updates"]').forEach((input) => { input.removeAttribute('name'); });
  }

  function markLine(line, quantity) {
    const input = line.querySelector('input');
    if (input) input.value = String(quantity);
    line.classList.toggle('is-removing', quantity === 0);
    line.classList.toggle('is-pending', quantity !== 0);
    const control = line.querySelector('[data-tap-quantity]');
    if (control) sync(control);
  }

  function setBusy() {
    if (root) root.classList.toggle('is-updating', Boolean(inFlight || pending.size));
  }

  function render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-cart-content]');
    const current = root.querySelector('[data-cart-content]');
    if (!next || !current) throw new Error('The cart could not be refreshed.');
    const active = document.activeElement;
    const activeLine = active && active.closest ? active.closest('[data-cart-line]') : null;
    const focus = activeLine && root.contains(activeLine)
      ? { id: activeLine.dataset.lineId, step: active.dataset.quantityStep || '' }
      : null;
    current.replaceWith(next);
    enhance(next);
    pending.forEach((quantity, id) => {
      const line = lineById(id);
      if (line) markLine(line, quantity);
    });
    const count = Number(next.dataset.itemCount);
    if (!Number.isNaN(count)) setCount(count);
    if (focus) {
      const line = lineById(focus.id);
      let target = line && focus.step ? line.querySelector(`[data-quantity-step="${focus.step}"]`) : null;
      if (target && target.disabled) target = line.querySelector('[data-quantity-step]:not(:disabled)');
      if (!target && line) target = line.querySelector('[data-remove-line]');
      if (!target) target = root.querySelector('h1');
      if (target) target.focus({ preventScroll: true });
    }
  }

  async function refresh() {
    if (!root || !sectionId) return;
    // No JSON Accept header here: with one, /cart answers with cart.js data instead of the section.
    const response = await fetch(`${cartUrl}?sections=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    const data = await response.json();
    if (!response.ok || !data || !data[sectionId]) throw new Error('The cart could not be refreshed.');
    render(data[sectionId]);
  }

  // Turn a wanted row total into per-line updates using the row's current line keys.
  function planRow(line, target) {
    let parts = [];
    try { parts = JSON.parse(line.dataset.lines || '[]'); } catch (ignored) { parts = []; }
    const updates = {};
    const total = parts.reduce((sum, part) => sum + part.quantity, 0);
    if (!parts.length || target === total) return updates;
    if (target > total) {
      updates[parts[0].key] = parts[0].quantity + (target - total);
      return updates;
    }
    let keep = target;
    parts.forEach((part) => {
      const quantity = Math.min(part.quantity, keep);
      keep -= quantity;
      if (quantity !== part.quantity) updates[part.key] = quantity;
    });
    return updates;
  }

  function flush() {
    if (inFlight) return inFlight; // the running save picks up queued changes when it finishes
    const updates = {};
    pending.forEach((quantity, id) => {
      const line = lineById(id);
      if (line) Object.assign(updates, planRow(line, quantity));
    });
    pending.clear();
    if (!Object.keys(updates).length) {
      setBusy();
      return Promise.resolve();
    }
    inFlight = (async () => {
      try {
        const response = await fetch(updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: [sectionId], sections_url: cartUrl })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const reason = typeof data.description === 'string' ? data.description
            : typeof data.message === 'string' ? data.message
            : 'Shopify could not update your cart.';
          throw new Error(reason);
        }
        lastError = null;
        showError('');
        if (data.sections && data.sections[sectionId]) render(data.sections[sectionId]);
        else await refresh();
        if (typeof data.item_count === 'number') setCount(data.item_count);
        const total = root.querySelector('[data-cart-total]');
        announce(total ? `Cart updated. Subtotal ${total.textContent.trim()}.` : 'Your cart is empty.');
      } catch (error) {
        lastError = error;
        try { await refresh(); } catch (ignored) { /* keep the page as it is */ }
        const message = error instanceof TypeError
          ? 'We could not reach the store. Check your connection and try again.'
          : error.message;
        showError(`${message} Your cart shows the latest saved quantities.`);
      } finally {
        inFlight = null;
        if (pending.size) flush();
        else setBusy();
      }
    })();
    setBusy();
    return inFlight;
  }

  function queue(line, quantity) {
    pending.set(line.dataset.lineId, quantity);
    markLine(line, quantity);
    setBusy();
    flush();
  }

  async function settle() {
    while (inFlight || pending.size) {
      await (inFlight || flush());
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const remove = target.closest('[data-remove-line]');
    if (remove && root && root.contains(remove)) {
      event.preventDefault();
      const line = remove.closest('[data-cart-line]');
      if (!line || line.classList.contains('is-removing')) return;
      announce(`Removing ${lineTitle(line)}…`);
      queue(line, 0);
      return;
    }

    const button = target.closest('[data-quantity-step]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;
    const control = button.closest('[data-tap-quantity]');
    const input = control && control.querySelector('input');
    if (!input) return;
    const line = control.closest('[data-cart-line]');
    const step = Number(input.step) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const floor = line ? 0 : Number(input.min || 1);
    const current = Number(input.value) || 0;
    const next = Math.min(max, Math.max(floor, current + Number(button.dataset.quantityStep) * step));
    if (next === current) return;
    if (!line || !root || !root.contains(line)) {
      input.value = String(next);
      sync(control);
      return;
    }
    if (next === 0) announce(`Removing ${lineTitle(line)}…`);
    queue(line, next);
  });

  document.addEventListener('keydown', (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-tap-quantity] input') : null;
    if (!input || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    event.preventDefault();
    const control = input.closest('[data-tap-quantity]');
    const button = control.querySelector(`[data-quantity-step="${event.key === 'ArrowUp' ? '1' : '-1'}"]`);
    if (button && !button.disabled) button.click();
  });

  // If the shopper reaches checkout while a change is still saving, finish saving first.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!root || !(form instanceof HTMLFormElement) || form.id !== 'CartForm' || !root.contains(form)) return;
    if (allowSubmit) return;
    if (!inFlight && !pending.size) return;
    event.preventDefault();
    const name = (event.submitter && event.submitter.name) || 'checkout';
    const button = form.querySelector('[name="checkout"]');
    if (button) {
      button.disabled = true;
      button.textContent = 'Saving your cart…';
    }
    await settle();
    const nextForm = root.querySelector('#CartForm');
    const nextButton = nextForm && nextForm.querySelector(`[name="${name}"]`);
    if (lastError || !nextForm || !nextButton) {
      const again = root.querySelector('[name="checkout"]');
      if (again) {
        again.disabled = false;
        again.textContent = 'Checkout';
      }
      return;
    }
    allowSubmit = true;
    nextForm.requestSubmit(nextButton);
  }, true);

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !root) return;
    allowSubmit = false;
    refresh().catch(() => window.location.reload());
  });

  window.TDACart = {
    refresh: () => (root && sectionId ? refresh() : Promise.resolve())
  };

  enhance(document);
})();
